<script setup lang="ts">
    import { useRoute, useRouter } from 'vue-router';
    import { inject } from 'vue';
    import DateRangePicker from '@shared/components/DateRangePicker.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import ConfirmButton from '@shared/components/ConfirmButton.vue';
    import Modal from '@shared/components/Modal.vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { deleteResponses, getSurveyStatisticsReport, buildQueryString } from '../SurveyStatistics.access';
    import {
        createSurveyFromPrevious,
        endSurvey as endSurveyRequest,
        removeSensitiveAnswers as removeSensitiveAnswersRequest,
        createReminderSegment,
    } from '../../Surveys.access';
    import { SurveyStatus, statusName, type QuestionFilter } from '../../SurveysTypes.d';
    import { computed, reactive } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { printWithAllCharts } from './print-charts';

    const props = defineProps<{
        dateRange: { start: string | null; end: string | null };
        selectedCultures: string[];
        showingList?: boolean;
        listLabel?: string;
        showingDetail?: boolean;
        surveyStatus?: number;
        surveyId?: string;
        surveyTitle?: string;
        eventId?: string | null;
        hasSensitiveAnswers?: boolean;
        showDeletedQuestions?: boolean;
        questionFilters?: QuestionFilter[];
        connectedIssueCount?: number;
    }>();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const state = reactive({ isBusy: false });

    const confirmModal = reactive<{
        show: boolean;
        title: string;
        buttonClass: string;
        action: (() => void) | null;
    }>({ show: false, title: '', buttonClass: 'btn-primary', action: null });

    function openConfirm(title: string, buttonClass: string, action: () => void) {
        confirmModal.title = title;
        confirmModal.buttonClass = buttonClass;
        confirmModal.action = action;
        confirmModal.show = true;
    }

    function confirmActionAndClose() {
        confirmModal.show = false;
        confirmModal.action?.();
    }

    const surveyStatusToTab: Partial<Record<number, statusName>> = {
        [SurveyStatus.Scheduled]: statusName.scheduled,
        [SurveyStatus.Draft]: statusName.drafts,
        [SurveyStatus.Active]: statusName.active,
        [SurveyStatus.Ended]: statusName.ended,
        [SurveyStatus.Template]: statusName.templates,
    };

    const surveysListTab = computed(() => (props.surveyStatus != null ? (surveyStatusToTab[props.surveyStatus] ?? statusName.ended) : statusName.ended));

    const showCommunicationDropdown = computed(() => !props.eventId || (props.connectedIssueCount ?? 0) > 0);

    const route = useRoute();
    const router = useRouter();

    const emit = defineEmits<{
        (e: 'update:dateRange', start: string | null, end: string | null): void;
    }>();

    function onDateRangeChange(start: string, end: string) {
        emit('update:dateRange', start || null, end || null);
    }

    function print() {
        printWithAllCharts();
    }

    async function exportReport(format: 'xlsx' | 'csv') {
        if (!props.surveyId) return;
        const qs = buildQueryString(props.dateRange, props.questionFilters ?? [], props.selectedCultures);

        try {
            const data = await getSurveyStatisticsReport(props.surveyId, qs, format, !!props.showDeletedQuestions);
            document.location.href = data.Url;
            bsAlert({ title: $Resources.ExportedSurveyHeading, content: $Resources.ExportedSurveyBody });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorCouldNotExportSurveyHeading, content: $Resources.ErrorCouldNotExportSurveyBody });
        }
    }

    async function cloneSurvey() {
        if (!props.surveyId || state.isBusy) return;
        state.isBusy = true;

        try {
            const data = await createSurveyFromPrevious(props.surveyId);
            document.location.href = `/Surveys/${data.SurveyId}?newsurvey=true`;
        } catch {
            state.isBusy = false;
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading, content: $Resources.ErrorCouldNotCloneSurvey });
        }
    }

    async function endSurvey() {
        if (!props.surveyId || state.isBusy) return;
        state.isBusy = true;

        try {
            await endSurveyRequest(props.surveyId);
            bsAlert({ title: $Resources.AlertSurveyEndedHeader, content: $Resources.AlertSurveyEndedBody });
            router.push({ name: 'Surveys', query: { tab: statusName.ended } });
        } catch {
            state.isBusy = false;
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorSurveyNotEndedHeader, content: $Resources.ErrorSurveyNotEndedBody });
        }
    }

    async function removeSensitiveAnswers() {
        if (!props.surveyId || state.isBusy) return;
        state.isBusy = true;

        try {
            await removeSensitiveAnswersRequest(props.surveyId);
            bsAlert({ title: $Resources.AlertSuccessHeading, content: $Resources.AlertEventSurveyRemoveAnswersBody });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading, content: $Resources.ErrorTechnicalIssuesBody });
        } finally {
            state.isBusy = false;
        }
    }

    async function sendReminder() {
        if (!props.surveyId || state.isBusy) return;
        state.isBusy = true;

        try {
            const segmentId = await createReminderSegment(props.surveyId, props.surveyTitle ?? '');
            document.location.href = `/Issues/New?surveyId=${props.surveyId}&segmentId=${segmentId}`;
        } catch {
            state.isBusy = false;
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading, content: $Resources.ErrorLabel });
        }
    }

    async function removeResponse() {
        const surveyId = route.params.surveyId as string;
        const responseId = route.params.responseId as string;
        const contactId = route.query.contactId as string | undefined;

        try {
            await deleteResponses(surveyId, [responseId]);
            bsAlert({ type: AlertTypes.Success });
            router.push(
                contactId
                    ? { name: 'SurveyStatisticsResponsesForContact', params: { surveyId, contactId } }
                    : { name: 'SurveyStatisticsResponses', params: { surveyId } }
            );
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        }
    }
</script>

<template>
    <nav class="navbar main-navbar hidden-print" role="navigation">
        <div class="container-fluid">
            <p class="navbar-title">
                <router-link :to="{ name: 'Surveys', query: { tab: surveysListTab } }">{{ $Resources.SurveysMenuItem }}</router-link>
                <span class="fw-400 c-light m-xs-l">/</span>
                <template v-if="showingList">
                    <router-link class="m-xs-l" :to="{ name: 'SurveyStatistics', params: { surveyId: route.params.surveyId } }">
                        {{ $Resources.SurveyStatisticsBreadcrumb }}
                    </router-link>
                    <span class="fw-400 c-light m-xs-l">/</span>
                    <router-link
                        class="m-xs-l"
                        v-if="showingDetail"
                        :to="
                            route.query.contactId
                                ? {
                                      name: 'SurveyStatisticsResponsesForContact',
                                      params: { surveyId: route.params.surveyId, contactId: route.query.contactId as string },
                                  }
                                : { name: 'SurveyStatisticsResponses', params: { surveyId: route.params.surveyId } }
                        ">
                        {{ listLabel }}
                    </router-link>
                    <span class="m-xs-l" v-else>{{ listLabel }}</span>
                    <template v-if="showingDetail">
                        <span class="fw-400 c-light m-xs-l">/</span>
                        <span class="m-xs-l">{{ $Resources.SurveyStatisticsSingleResponseBreadcrumb }}</span>
                    </template>
                </template>
                <template v-else>
                    <span class="m-xs-l">{{ $Resources.SurveyStatisticsBreadcrumb }}</span>
                </template>
            </p>

            <div v-show="!showingList" id="teleported-target-tabs" class="m-auto-r"></div>

            <div v-show="!showingList">
                <ul class="list-unstyled nav navbar-nav">
                    <li>
                        <DateRangePicker :dateFilter="dateRange" @onDateRangeChange="onDateRangeChange" />
                    </li>

                    <li v-if="showCommunicationDropdown">
                        <BSDropdownButton menuClass="dropdown-menu-right">
                            <template #toggle>
                                <span class="glyphicon glyphicon-envelope m-xs-r"></span>
                                <span class="navbar-label">{{ $Resources.SurveyCommunicationButton }}</span>
                            </template>
                            <template #content>
                                <li v-if="!eventId">
                                    <a :href="`/Issues/New?surveyId=${surveyId}`">
                                        <span class="glyphicon glyphicon-envelope m-xs-r"></span>
                                        {{ $Resources.SurveyCommunicationSendInvitationButton }}
                                    </a>
                                </li>
                                <li v-if="(connectedIssueCount ?? 0) > 0">
                                    <a role="button" @click="sendReminder()">
                                        <span class="glyphicon glyphicon-bell m-xs-r"></span>
                                        {{ $Resources.SurveyCommunicationSendReminderButton }}
                                    </a>
                                </li>
                            </template>
                        </BSDropdownButton>
                    </li>

                    <li v-if="contextAccess.visibleInSurveyMenu('Export')" class="dropdown">
                        <BSDropdownButton menuClass="dropdown-menu-right" closeOnClickInside>
                            <template #toggle>
                                <span class="glyphicon glyphicon-export m-xs-r"></span>
                                <span class="navbar-label">{{ $Resources.ExportButton }}</span>
                            </template>
                            <template #content>
                                <li role="presentation" class="dropdown-header">{{ $Resources.ExportToLabel }}:</li>
                                <li>
                                    <a href="javascript:void(0)" @click="exportReport('xlsx')">{{ $Resources.ExportStatisticsToExcelButton }}</a>
                                </li>
                                <li>
                                    <a href="javascript:void(0)" @click="exportReport('csv')">{{ $Resources.ExportStatisticsToCsvButton }}</a>
                                </li>
                            </template>
                        </BSDropdownButton>
                    </li>

                    <li class="dropdown">
                        <BSDropdownButton menuClass="dropdown-menu-right">
                            <template #toggle>
                                <span class="glyphicon glyphicon-option-vertical m-xs-r"></span>
                                {{ $Resources.NavbarMoreButton }}
                            </template>
                            <template #content>
                                <li v-if="contextAccess.visibleInSurveyMenu('Edit')">
                                    <a :href="`/Surveys/${surveyId}`" role="button">
                                        <span class="glyphicon glyphicon-edit"></span>
                                        {{ $Resources.EditSurveyButton }}
                                    </a>
                                </li>
                                <li v-if="contextAccess.visibleInSurveyMenu('Clone')">
                                    <a href="javascript:void(0)" role="button" @click="openConfirm($Resources.SurveyCloneButton, 'btn-primary', cloneSurvey)">
                                        <span class="glyphicon glyphicon-duplicate"></span>
                                        {{ $Resources.SurveyCloneButton }}
                                    </a>
                                </li>
                                <li v-if="contextAccess.visibleInSurveyMenu('Print')">
                                    <a href="javascript:void(0)" role="button" @click="print()">
                                        <span class="glyphicon glyphicon-print"></span>
                                        {{ $Resources.PrintReportBtn }}
                                    </a>
                                </li>
                                <li v-if="surveyStatus !== SurveyStatus.Active && contextAccess.visibleInSurveyMenu('PublishSurvey')">
                                    <a :href="`/Surveys/${surveyId}?tab=publish`">
                                        <span class="fa fa-calendar-o"></span>
                                        {{ $Resources.SurveysRepublishButton }}
                                    </a>
                                </li>
                                <li v-if="surveyStatus === SurveyStatus.Active && contextAccess.visibleInSurveyMenu('EndSurvey')">
                                    <a href="javascript:void(0)" role="button" @click="openConfirm($Resources.SurveysEndButton, 'btn-danger', endSurvey)">
                                        <span class="fa fa-calendar-times-o"></span>
                                        {{ $Resources.SurveysEndButton }}
                                    </a>
                                </li>
                                <li v-if="hasSensitiveAnswers">
                                    <a
                                        href="javascript:void(0)"
                                        role="button"
                                        @click="openConfirm($Resources.EventSurveyRemoveAnswersHeading, 'btn-danger', removeSensitiveAnswers)">
                                        <span class="fa fa-trash"></span>
                                        {{ $Resources.EventSurveyRemoveAnswersHeading }}
                                    </a>
                                </li>
                            </template>
                        </BSDropdownButton>
                    </li>
                </ul>
            </div>

            <div v-show="showingDetail" class="m-auto-l">
                <ul class="list-unstyled nav navbar-nav">
                    <li>
                        <button type="button" class="btn btn-default" @click="print()">
                            <span class="glyphicon glyphicon-print m-xs-r"></span>
                            {{ $Resources.PrintReportBtn }}
                        </button>
                    </li>
                    <li>
                        <ConfirmButton :buttonText="$Resources.DeleteButton" @confirm="removeResponse">
                            <span class="fa fa-trash m-xs-r"></span>
                            {{ $Resources.DeleteButton }}
                        </ConfirmButton>
                    </li>
                </ul>
            </div>
        </div>
    </nav>

    <Modal size="sm" :show="confirmModal.show" :title="confirmModal.title" @close="confirmModal.show = false">
        <template #content>
            <p>{{ $Resources.GeneralConfirmMessage }}</p>
        </template>
        <template #button>
            <button type="button" class="btn" :class="confirmModal.buttonClass" :disabled="state.isBusy" @click="confirmActionAndClose()">
                {{ $Resources.ConfirmButton }}
            </button>
        </template>
    </Modal>
</template>
